import {useEffect, useRef} from 'react';
import {useAuth} from 'react-oidc-context';

import userManager from './userManager';
import {getFromLocalStorage, NewLocalStorage, routeToLogin} from '../lib/utils';
import {isImpersonating} from '../lib/impersonation';
import {
  LOGOUT_BROADCAST_KEY,
  OIDC_USER_STORAGE_KEY,
  clearLocalSession,
  clearSignedOutFlag,
  hasRealigned,
  isLoggingOut,
  loadStoredUser,
  markRealigned,
  markSignedOut,
  roleFromToken,
  subjectOf,
  syncProfileFromToken
} from './sessionSync';

// Keeps every tab on ONE session.
//
// Both halves of the session are now shared in localStorage: the OIDC user
// (`oidc.user:…`, written by oidc-client-ts) and the app profile
// (`epadhaiBaseData_v2` — userId/role/tenantName, what the header, sidebar and
// AuthRoute render from). Each tab still holds its own in-memory copy of the
// user, so this guard reconciles three things:
//
//   1. Adopt   — the shared store has a user this tab doesn't (another tab
//                logged in, or renewed and rotated the refresh token).
//   2. Drop    — the shared store has no user (another tab logged out).
//   3. Realign — the profile describes a different account than the stored
//                token. The TOKEN wins and the profile is rewritten from it;
//                the old "log the tab out and re-login" remedy would now tear
//                down the shared session other tabs are using.
//
// Rule of thumb: the stored OIDC user is the source of truth, the profile is
// derived from it, and no tab ever runs with the two describing different
// people (the failure that rendered an admin sidebar while every API call
// 403'd on a tenant user's bearer token).
//
// Skipped while impersonating: impersonation keeps its target token in
// sessionStorage and never rewrites the shared profile, so a reload or adopt
// here would only disrupt the admin's impersonation tab. Remote LOGOUT is
// still honoured — the impersonation hangs off the admin's session.
const SessionIdentityGuard = () => {
  const auth = useAuth();

  // Read inside listeners without re-subscribing on every token change.
  const authRef = useRef(auth);
  authRef.current = auth;

  // Guards against two reload paths firing at once (e.g. the profile-changed
  // event and the token-loaded effect landing in the same tick).
  const navigatingRef = useRef(false);

  useEffect(() => {
    const handleRemoteLogout = () => {
      if (navigatingRef.current) return;
      navigatingRef.current = true;
      // Land on the login page signed OUT — don't let the auto-redirect race
      // the other tab's end_session and sign us back in. See markSignedOut.
      markSignedOut();
      clearLocalSession();
      // Hard nav, not removeUser(): the shared store is already empty and
      // calling removeUser here would re-fire the storage event in tabs that
      // are mid-teardown.
      routeToLogin();
    };

    // Pull the shared user into this tab. Same account → hand it to
    // oidc-client-ts so React state, the API layer and the renew timers all
    // move to the live token in place. Different account (or this tab had
    // none) → full reload, because roles, Redux state and closure-captured
    // tokens all have to be rebuilt. Same reasoning as CallbackPage's
    // window.location.replace; see auth_state_lifecycle.md.
    const adoptStoredUser = async () => {
      if (navigatingRef.current) return;
      const stored = await loadStoredUser();
      if (!stored?.access_token) return;

      const currentToken = authRef.current.user?.access_token || '';
      if (stored.access_token === currentToken) return;

      const storedSub = subjectOf(stored.access_token);
      const currentSub = subjectOf(currentToken);

      // Profile follows the token, always.
      if (storedSub && storedSub !== (getFromLocalStorage() || {}).userId) {
        syncProfileFromToken(stored.access_token);
      }

      if (currentSub && storedSub === currentSub) {
        // Plain token refresh from another tab — swap it in silently.
        await userManager.events.load(stored, true);
        return;
      }

      navigatingRef.current = true;
      window.location.reload();
    };

    const onStorage = (e) => {
      if (e.storageArea && e.storageArea !== window.localStorage) return;
      // This tab started the logout. Everything arriving now is the echo of
      // the other tabs tearing down — reacting to it would flag this tab
      // signed-out and race its own signoutRedirect. See markLoggingOut.
      if (isLoggingOut()) return;

      // Explicit logout from another tab.
      if (e.key === LOGOUT_BROADCAST_KEY) {
        if (e.newValue) handleRemoteLogout();
        return;
      }

      // A `null` key means the whole store was cleared.
      if (e.key === null) {
        if (!authRef.current.isAuthenticated) return;
        handleRemoteLogout();
        return;
      }

      if (e.key === OIDC_USER_STORAGE_KEY) {
        // signoutRedirect()/removeUser() in another tab empties this slot.
        if (!e.newValue) {
          handleRemoteLogout();
          return;
        }
        if (isImpersonating()) return;
        adoptStoredUser();
        return;
      }

      // Profile rewritten elsewhere (a login finishing in another tab writes
      // the token first, then the profile — this covers the second write).
      if (e.key === NewLocalStorage && e.newValue) {
        if (isImpersonating()) return;
        adoptStoredUser();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // An authenticated tab is, by definition, no longer signed out. Clearing the
  // marker here (app-wide — this guard is mounted above the router) stops it
  // lingering in a tab that adopted another tab's login: left set, it would
  // suppress the auto-signin on some later, unrelated session expiry and show
  // the "signed out" card with a manual button instead of re-authing silently.
  useEffect(() => {
    if (auth.isAuthenticated) clearSignedOutFlag();
  }, [auth.isAuthenticated]);

  // In-tab check on every token load, including silent renews: a renew rides
  // the browser-wide Zitadel SSO cookie, so with several accounts logged in at
  // the IdP it can hand this tab a token for a DIFFERENT account than the one
  // it started with. Realign the profile rather than logging out.
  //
  // Role is compared as well as subject: the route guards and the sidebar read
  // `role` off the profile, so a same-user token whose roles changed (new
  // authorization granted, impersonation exited) still has to be re-derived or
  // the UI keeps gating on the old one.
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) return;
    if (isImpersonating()) return;
    if (navigatingRef.current) return;

    const token = auth.user.access_token;
    const profile = getFromLocalStorage() || {};

    // No profile at all — this tab adopted a session whose signin callback ran
    // somewhere else (or a logout cleared the profile but left the token).
    // Write it; nothing is rendering stale data yet, so no reload needed.
    if (!profile.userId) {
      syncProfileFromToken(token);
      return;
    }

    const sub = subjectOf(token);
    const role = roleFromToken(token);
    const identityDrifted = sub && sub !== profile.userId;
    const roleDrifted = role && role !== profile.role;
    if (!identityDrifted && !roleDrifted) return;

    syncProfileFromToken(token);

    // Reload so roles, Redux state and any closure-captured token are rebuilt
    // against the new identity — but only once per tab. If we land here again
    // after having already reloaded, something is rewriting the profile behind
    // us and a second reload would just spin on a blank page.
    if (hasRealigned()) return;
    markRealigned();
    navigatingRef.current = true;
    window.location.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user]);

  return null;
};

export default SessionIdentityGuard;
